import { FC, useEffect, useRef, useState } from 'react';
import { translate } from '../../i18n';
import { usePaymentMiddlewareClient } from '../../browser/PaymentMiddlewareClientContext';
import { CARD_DISPLAY_DEBOUNCE_MS, displayQuery, type CardDisplay } from '../../browser/cardDisplay';
import { theme } from '../../theme';
import { PaymentMethodCard } from '../PaymentMethodCard';
import { cardPreviewStyles } from './styles';

const PENDING_GRADIENT: [string, string] = [
  theme.colors.palette.grey800,
  theme.colors.palette.grey900,
];

export interface CardPreviewProps {
  cardNumber: string;
  holderName: string;
  expiryMonth?: string;
  expiryYear?: string;
  testID?: string;
}

const formatPan = (digits: string): string => digits.replace(/(.{4})/g, '$1 ').trim();

const formatExpiry = (month?: string, year?: string): string => {
  if (!month && !year) return '';
  return `${month || 'MM'}/${year || 'AA'}`;
};

/**
 * CardPreview — Preview en vivo de la tarjeta que se está agregando.
 *
 * Sin número cargado muestra el placeholder gris. Con dígitos pide el display
 * al order-service (mismo canal que el resto del pago) y pinta banco, red y colores.
 */
export const CardPreview: FC<CardPreviewProps> = ({
  cardNumber,
  holderName,
  expiryMonth,
  expiryYear,
  testID = 'card-preview',
}) => {
  const paymentClient = usePaymentMiddlewareClient();
  const missingClientWarned = useRef(false);
  const digits = cardNumber.replace(/\D/g, '');
  const query = displayQuery(digits);
  const [display, setDisplay] = useState<CardDisplay | null>(null);

  useEffect(() => {
    if (!query) {
      setDisplay(null);
      return;
    }
    if (!paymentClient) {
      if (import.meta.env.DEV && !missingClientWarned.current) {
        missingClientWarned.current = true;
        console.warn(
          '[CardPreview] Sin PaymentMiddlewareClient (¿falta payment-init del checkout?). No se llama cards/display.',
        );
      }
      setDisplay(null);
      return;
    }

    // Mientras llega la consulta se mantiene la cara anterior, para no
    // parpadear en gris con cada dígito. Si la nueva falla o no identifica
    // la tarjeta, ahí sí se suelta el banco del número previo.
    let cancelled = false;
    const timer = window.setTimeout(() => {
      paymentClient
        .displayCard(query)
        .then((next) => {
          if (!cancelled) setDisplay(next);
        })
        .catch(() => {
          if (!cancelled) setDisplay(null);
        });
    }, CARD_DISPLAY_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [query, paymentClient]);

  if (digits.length === 0) {
    return (
      <div style={cardPreviewStyles.emptyContainer} data-testid={testID}>
        <div style={cardPreviewStyles.emptyExpiry}>
          {translate('paymentsUI.cardPreview.placeholderExpiry')}
        </div>
        <div style={cardPreviewStyles.emptyCardNumber}>
          {translate('paymentsUI.cardPreview.placeholderNumber')}
        </div>
        <div style={cardPreviewStyles.emptyHolderName}>
          {translate('paymentsUI.cardPreview.placeholderHolderName')}
        </div>
      </div>
    );
  }

  const networkKnown = Boolean(
    display && display.icon !== 'generic' && display.networkLabel && display.networkLabel !== 'Tarjeta',
  );
  const issuerName = display?.issuerLabel && display.issuerLabel !== 'Tarjeta' ? display.issuerLabel : '';

  return (
    <PaymentMethodCard
      issuerName={issuerName}
      last4={digits.slice(-4)}
      expiry={formatExpiry(expiryMonth, expiryYear)}
      pan={formatPan(digits)}
      holderName={holderName || translate('paymentsUI.cardPreview.placeholderHolderName')}
      network={networkKnown ? display!.networkLabel : ''}
      networkVariant={networkKnown || issuerName ? display?.networkVariant ?? '' : ''}
      icon={networkKnown ? display!.icon : undefined}
      gradientColors={
        display ? [display.colorStart, display.colorEnd] : PENDING_GRADIENT
      }
      textColor={display?.textColor}
      isSelected
      testID={testID}
    />
  );
};
